/**
 * HomeLiveTV — Live TV rail (v1.85).
 *
 * Circular avatar tiles (Instagram-Stories style) for 24×7 LIVE channels.
 * Each tile opens the YT channel's /live URL in a new tab — auto-redirects
 * to the current live stream.
 *
 * Layout: horizontal scroll, ~80px avatar with red LIVE dot + name below.
 * Categories: news, business, music. Country code shown subtly on hover.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { ChevronLeft, ChevronRight, Radio } from "lucide-react";
import { readSwrCache, writeSwrCache } from "../lib/swrCache";

export default function HomeLiveTV() {
  // v2.19 — SWR hydration. Channels list is stable (28 curated channels)
  // so seeding from localStorage is safe even on slow first paint.
  const cached = readSwrCache("live-tv");
  const [items, setItems] = useState(cached || []);
  const [loading, setLoading] = useState(!cached);
  const scrollRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/live-tv");
        const arr = r.data?.items || [];
        setItems(arr);
        if (arr.length) writeSwrCache("live-tv", arr);
      } catch (e) {
        // Keep cached items on error
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const scroll = (dir) => {
    if (!scrollRef.current) return;
    const w = scrollRef.current.clientWidth;
    scrollRef.current.scrollBy({ left: dir * w * 0.7, behavior: "smooth" });
  };

  if (loading || items.length === 0) return null;

  return (
    <section
      className="px-6 lg:px-10 mt-10 max-w-[1440px] mx-auto"
      data-testid="home-live-tv"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Radio className="w-5 h-5 text-red-500" />
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          </div>
          <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight">
            Live Now
          </h2>
          <span className="text-[11px] font-bold uppercase tracking-wider text-red-500 bg-red-500/10 border border-red-500/30 px-2 py-0.5 rounded">
            On Air
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => scroll(-1)}
            data-testid="live-tv-scroll-left"
            aria-label="Scroll left"
            className="p-1.5 rounded-full bg-white/[0.06] hover:bg-white/[0.12] text-white/80"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => scroll(1)}
            data-testid="live-tv-scroll-right"
            aria-label="Scroll right"
            className="p-1.5 rounded-full bg-white/[0.06] hover:bg-white/[0.12] text-white/80"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex gap-5 overflow-x-auto pb-3 scrollbar-thin scrollbar-thumb-white/10 snap-x snap-mandatory"
        data-testid="live-tv-list"
      >
        {items.map((ch, idx) => (
          <a
            key={ch.channel_id}
            href={ch.watch_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`live-tv-tile-${idx}`}
            className="group shrink-0 w-[88px] sm:w-[96px] snap-start flex flex-col items-center"
            title={`${ch.name} — Live on YouTube`}
          >
            {/* Avatar with red gradient ring (Instagram-Stories style) */}
            <div className="relative">
              <div className="p-[2.5px] rounded-full bg-gradient-to-br from-red-500 via-red-600 to-amber-500 group-hover:scale-105 transition-transform">
                <div className="p-[2px] rounded-full bg-black">
                  {ch.thumb ? (
                    <img
                      src={ch.thumb}
                      alt={ch.name}
                      loading="lazy"
                      className="w-[72px] h-[72px] sm:w-[80px] sm:h-[80px] rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-[72px] h-[72px] sm:w-[80px] sm:h-[80px] rounded-full bg-neutral-800 flex items-center justify-center">
                      <Radio className="w-7 h-7 text-white/40" />
                    </div>
                  )}
                </div>
              </div>
              {/* Red LIVE dot */}
              <div className="absolute -bottom-0.5 -right-0.5 px-1.5 py-0.5 rounded-full bg-red-500 text-[9px] font-bold text-white flex items-center gap-0.5 shadow-lg">
                <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                LIVE
              </div>
            </div>
            <div className="mt-2 text-center w-full">
              <p className="text-[11px] font-semibold text-white truncate" title={ch.name}>
                {ch.name}
              </p>
              <p className="text-[9px] text-white/40 uppercase tracking-wide">
                {ch.category === "business"
                  ? "Business"
                  : ch.category === "music"
                  ? "Music"
                  : "News"}
              </p>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}
